import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { useQuery } from '@tanstack/react-query';
import {
  PendingItems,
  WalletCreationStatus,
  WalletCustomerCardData,
  WalletDashboardStatus,
  WalletEntityType,
  WalletKycCustomer,
  WalletKycStatus,
  WalletSetupResume,
  WalletSetupStep,
  WalletStatusCardData,
} from '../../../types/wallet/Walletkyc';
import getWalletKycStatus from '../kyc/getWalletKycStatus';

//TODO: REMOVE WHEN CORPORATE WALLET IS AVAILABLE
const PREFERRED_ENTITY: WalletEntityType = 'individual';

const walletStatusesWithCreatedWallet: WalletCreationStatus[] = [
  'active',
  'suspended',
  'closed',
];

const setupPendingItemToCorporateWalletStep: Partial<
  Record<PendingItems, WalletSetupStep>
> = {
  corporate_profile: 0,
  kyc_profile: 0,
  director_setup: 2,
  corporate_documents_upload: 3,
};

/// this gets initial step for individual wallet setup
const getIndividualWalletStep = (
  customer: WalletKycCustomer
): WalletSetupStep => {
  const TARGETS = ['rejected', 'not_submitted'];
  if (
    TARGETS.includes(customer.kyc.nin_status) ||
    TARGETS.includes(customer.kyc.bvn_status)
  )
    return 1;
  if (TARGETS.includes(customer.kyc.address_status)) return 2;

  if (customer.wallet_status === 'not_created') return 3;

  return 0;
};

const customerHasWallets = (customer: WalletKycCustomer) =>
  (customer.wallet_context?.wallets?.length || 0) > 0 ||
  walletStatusesWithCreatedWallet.includes(customer.wallet_status);

const getSetupResume = (
  customer: WalletKycCustomer
): WalletSetupResume | null => {
  let initialWalletSetupStep: WalletSetupStep = 0;

  if (customer.entity_type == 'corporate') {
    const possibleSteps = customer.pending_items
      .map((item) =>
        customer.entity_type == 'corporate'
          ? setupPendingItemToCorporateWalletStep[item]
          : null
      ) /// MANAGE INITIAL STEP INTERNALLY FOR INDIVIDUAL
      .filter((step): step is WalletSetupStep => typeof step === 'number');

    initialWalletSetupStep = Math.min(...possibleSteps) as WalletSetupStep;
  } else if (customer.entity_type == 'individual') {
    initialWalletSetupStep = getIndividualWalletStep(customer);
  }

  return {
    entity_type: customer.entity_type,
    entity_id: customer.entity_id,
    customer_id: customer.customer_id,
    initial_step: initialWalletSetupStep,
    pending_items: customer.pending_items,
  };
};

const getCustomerLabel = (customer: WalletKycCustomer) =>
  customer.entity_type === 'corporate' ? 'Corporate Wallet' : 'Personal Wallet';

const pickPreferredCustomer = (customers: WalletKycCustomer[]) =>
  customers.find(
    (customer) =>
      customer.entity_type === PREFERRED_ENTITY && customerHasWallets(customer)
  ) ||
  customers.find(customerHasWallets) ||
  customers.find((customer) => Boolean(getSetupResume(customer))) ||
  customers.find((customer) => customer.entity_type === PREFERRED_ENTITY) ||
  customers[0] ||
  null;

const toWalletCardData = (
  customer: WalletKycCustomer | null
): WalletCustomerCardData | null => {
  if (!customer) return null;
  const wallets = customer.wallet_context?.wallets || [];
  const setupResume = getSetupResume(customer);

  return {
    label: getCustomerLabel(customer),
    customer_id: customer.customer_id,
    entity_type: customer.entity_type,
    wallet_status: customer.wallet_status,
    wallet_id: customer.wallet_id,
    pending_items: customer.pending_items || [],
    wallet_context: customer.wallet_context,
    overall_status:
      customer.kyc_status?.overall_status ||
      customer.kyc?.overall_status ||
      'incomplete',
    total_wallets: customer.wallet_context?.total_wallets || wallets.length,
    wallets,
    needs_setup_resume: Boolean(setupResume),
    setup_resume: setupResume,
  };
};

const walletCardHasWallets = (walletCardData: WalletStatusCardData) =>
  walletStatusesWithCreatedWallet.includes(walletCardData.wallet_status) ||
  walletCardData.wallets.length > 0;

export const useUserWalletStatus = () => {
  const { signedAccessToken: authToken } = useSelector(
    (state: RootState) => state.userSession
  );

  const walletQuery = useQuery({
    queryKey: ['wallet-status', authToken],
    queryFn: async (): Promise<WalletDashboardStatus | null> => {
      if (!authToken) return null;

      try {
        const walletStatus = await getWalletKycStatus({
          authToken,
        });

        const customers = (walletStatus as WalletKycStatus).customers || [];

        const relatedCustomers = customers
          .map(toWalletCardData)
          .filter((customer): customer is WalletCustomerCardData =>
            Boolean(customer)
          );

        const preferredCustomer = toWalletCardData(
          pickPreferredCustomer(customers)
        );

        if (!preferredCustomer) return null;

        return {
          ...preferredCustomer,
          related_customers: relatedCustomers,
        };
      } catch (err: any) {
        if (err?.status === 404 || err?.status === 500) return null;
        console.error(err.message);
        throw err;
      }
    },
    enabled: Boolean(authToken),
  });

  const level = !walletQuery.data
    ? 0
    : walletCardHasWallets(walletQuery.data)
      ? 2
      : 1;
  return { level, walletQuery };
};
